import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'

import classnames from 'classnames'

import useTracker from 'src/lib/js/hooks/useTracker'
import { NearbyRestaurantSearchResultRestaurantType } from 'src/public/components/default_template/restaurant_profile/profileUtils'

import Image from 'shared/components/common/Image'

import { resources } from 'config'

import RXConfig from './RXConfig'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

export const formatRXName = (name: string): string => {
  return name.replace(/\\r\\n/g, ' - ')
}

const genericRXImage = {
  src: 'toastLocal/common/generic-restaurant-1102.png',
  srcSet: [
    'toastLocal/common/generic-restaurant-276.png 276w',
    'toastLocal/common/generic-restaurant-551.png 551w',
    'toastLocal/common/generic-restaurant-1102.png 1102w'
  ],
  alt: 'Restaurant banner'
}

const ConditionalWrapper = ({
  condition,
  wrapper,
  children
}: {
  condition: boolean
  wrapper: (n: React.ReactNode) => JSX.Element
  children: JSX.Element
}): JSX.Element => (condition ? wrapper(children) : children)

export const hasOORXProfile = (
  rx: NearbyRestaurantSearchResultRestaurantType
) => (rx.hasOnlineOrderingModule || rx.hasToastLocalModule) && rx.shortUrl

type Props = {
  restaurant: NearbyRestaurantSearchResultRestaurantType
  utmContent?: string
  className?: string
  linkToDineIn: boolean
}

const RXCard = ({ restaurant, className, linkToDineIn }: Props) => {
  const tracker = useTracker()

  const imageData = useMemo(() => {
    if (restaurant.imageUrl) {
      return { src: restaurant.imageUrl, cdnHost: resources.tlAssetUrl }
    }
    return { ...genericRXImage }
  }, [restaurant.imageUrl])

  const dineInProfileUrl = useMemo(
    () => (restaurant.shortUrl ? `/local/${restaurant.shortUrl}` : ''),
    [restaurant.shortUrl]
  )
  const profileUrl = useMemo(
    () =>
      linkToDineIn
        ? // if told to do so (i.e. dine-in filter was selected), link to the dine-in page
          dineInProfileUrl
        : // otherwise, link to the order page if rx has OO, else do not link
        hasOORXProfile(restaurant)
        ? `/local/order/${restaurant.shortUrl}`
        : '',
    [dineInProfileUrl, linkToDineIn, restaurant]
  )

  return (
    <div
      className={classnames(
        'rx-card-container',
        !profileUrl && 'disabled',
        className
      )}
    >
      <ConditionalWrapper
        condition={!!profileUrl}
        wrapper={(children) => (
          <Link
            to={{ pathname: profileUrl, state: { fromSearch: true } }}
            className='oo-link'
            onClick={() =>
              tracker.track(TrackingEvents.PAGE_VIEW, { url: profileUrl })
            }
          >
            {children}
          </Link>
        )}
      >
        <div className='rx-card-wrapper'>
          <div className='rx-card'>
            <div className='rx-image-wrapper'>
              {imageData && (
                <Image alt='' {...imageData} className='rx-image' />
              )}
            </div>

            <div className='rx-details'>
              <div className='rx-header'>
                <h3 className='rx-name'>
                  {formatRXName(restaurant.name || '')}
                </h3>
                <RXConfig config={restaurant} />
              </div>
            </div>
          </div>
        </div>
      </ConditionalWrapper>
      {
        // if no profileUrl or dineInProfileUrl, do not show the 'More about' link.
        // in practice, this only happens in preprod because dineInProfileUrl is only empty if rx has no shortUrl.
        // shortUrls are generated for all rxs in prod, but they aren't consistently copied over to preprod.
        profileUrl || dineInProfileUrl ? (
          <Link
            // if no profileUrl (i.e. not using dine-in filter and rx does not have OO), link to the dine-in profile page
            to={{
              pathname: profileUrl ? profileUrl : dineInProfileUrl,
              state: { fromSearch: true }
            }}
            className='link-to-rx-profile'
          >
            More about{' '}
            {restaurant.name
              ? formatRXName(restaurant.name)
              : 'this restaurant'}
          </Link>
        ) : null
      }
    </div>
  )
}

export default RXCard
